import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AxiosError } from "axios";
import "../styles/shared/DeliveryPreferenceModal.css";
import "../styles/shared/LoadingState.css";
import { setApiAuthToken } from "../services/api";
import {
  adminLogin as adminLoginRequest,
  completeProfile as completeProfileRequest,
  googleLogin as googleLoginRequest,
  requestPhoneOtp as requestPhoneOtpRequest,
  verifyPhoneOtp as verifyPhoneOtpRequest,
  updateDeliveryPreference as updateDeliveryPreferenceRequest,
} from "../services/authService";
import { fetchAddresses } from "../services/accountService";
import PincodeServiceChecker from "../components/shared/PincodeServiceChecker";
import { readSelectedAddress, storeSelectedAddress } from "../utils/selectedAddress";
import useBodyScrollLock from "../hooks/useBodyScrollLock";

import { useProcessing } from "./ProcessingContext";
import { AuthUser, DeliveryMode } from "../types/store";
import { normalizeIndianMobileNumber } from "../utils/phoneNumber";

interface AuthActionResult<T = void> {
  data?: T;
  error?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  loading: boolean;
  googleLogin: (credential: string) => Promise<AuthActionResult>;
  requestPhoneOtp: (phoneNumber: string) => Promise<AuthActionResult<{
    phoneNumber: string;
    expiresInSeconds: number;
    sent: boolean;
  }>>;
  verifyPhoneOtp: (phoneNumber: string, otp: string) => Promise<AuthActionResult>;
  adminLogin: (email: string, password: string) => Promise<AuthActionResult>;
  completeProfile: (payload: ProfileCompletionPayload) => Promise<AuthActionResult>;
  updateDeliveryPreference: (mode: DeliveryMode) => Promise<AuthActionResult>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AUTH_STORAGE_KEY = "eldoo-auth-user";
const TOKEN_STORAGE_KEY = "eldoo-token";
const PROFILE_COMPLETION_SKIP_STORAGE_PREFIX = "eldoo-profile-completion-skipped";
const DELIVERY_PROMPT_COMPLETED_SESSION_PREFIX = "eldoo-delivery-preference-completed";
const PINCODE_CHECKER_LOGIN_KEY = "eldoo-login-pincode-checker-shown";

const decodeJwtPayload = (token: string) => {
  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decodedPayload = window.atob(
      normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, "="),
    );
    return JSON.parse(decodedPayload) as { role?: string; exp?: number };
  } catch {
    return null;
  }
};

const isTokenValidForUser = (token: string, user: AuthUser) => {
  if (!token.trim()) {
    return false;
  }

  const payload = decodeJwtPayload(token);
  // Keep the local session until the user explicitly logs out. The server remains
  // responsible for authorizing each API request, so an expired JWT must not erase
  // the customer's saved login state or force an unexpected redirect to /login.
  return !payload?.role || payload.role === user.role;
};

const getDisplayName = (user: AuthUser | null) => {
  const fullName = user?.fullName?.trim();
  if (fullName) {
    return fullName;
  }

  return "";
};

const shouldRequireCustomerName = (user: AuthUser | null) =>
  Boolean(user?.role === "ROLE_CUSTOMER" && (!getDisplayName(user) || !user.phoneNumber?.trim()));

const getProfileCompletionSkipSessionKey = (user: AuthUser) =>
  `${PROFILE_COMPLETION_SKIP_STORAGE_PREFIX}:${user.email || user.phoneNumber || user.id || "customer"}`;

const hasSkippedProfileCompletionThisSession = (user: AuthUser) =>
  window.sessionStorage.getItem(getProfileCompletionSkipSessionKey(user)) === "true";

const markProfileCompletionSkippedThisSession = (user: AuthUser) => {
  window.sessionStorage.setItem(getProfileCompletionSkipSessionKey(user), "true");
};

const getDeliveryPromptCompletedSessionKey = (user: AuthUser) =>
  `${DELIVERY_PROMPT_COMPLETED_SESSION_PREFIX}:${user.email || user.phoneNumber || user.id || "customer"}`;

const hasCompletedDeliveryPromptThisSession = (user: AuthUser) =>
  window.sessionStorage.getItem(getDeliveryPromptCompletedSessionKey(user)) === "true";

const markDeliveryPromptCompletedThisSession = (user: AuthUser) => {
  window.sessionStorage.setItem(getDeliveryPromptCompletedSessionKey(user), "true");
};

const shouldShowDeliveryPrompt = (user: AuthUser | null, requireProfile: boolean) =>
  Boolean(
    user?.role === "ROLE_CUSTOMER" &&
      !requireProfile &&
      !hasCompletedDeliveryPromptThisSession(user) &&
      !window.location.pathname.startsWith("/admin"),
  );

const restrictLettersOnly = (value: string) => value.replace(/[^A-Za-z\s.'-]/g, "");

interface ProfileCompletionPayload {
  fullName: string;
  phoneNumber: string;
  email?: string;
}

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const responseMessage = error.response?.data?.message;
    if (typeof responseMessage === "string" && responseMessage.trim()) {
      return responseMessage;
    }

    const fieldErrors = error.response?.data?.errors;
    if (fieldErrors && typeof fieldErrors === "object") {
      const firstFieldMessage = Object.values(fieldErrors).find(
        (value) => typeof value === "string" && value.trim(),
      );
      if (typeof firstFieldMessage === "string") {
        return firstFieldMessage;
      }
    }
  }

  return fallback;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeliveryPreferenceModal, setShowDeliveryPreferenceModal] = useState(false);
  const [showPincodeCheckerModal, setShowPincodeCheckerModal] = useState(false);
  const [deliveryPincodePreview, setDeliveryPincodePreview] = useState<string | null>(null);
  const [showGuestPincodeCheckerModal, setShowGuestPincodeCheckerModal] = useState(false);
  const [guestDeliveryPincodePreview, setGuestDeliveryPincodePreview] = useState<string | null>(null);
  const [showProfileCompletionModal, setShowProfileCompletionModal] = useState(false);
  const [pendingDeliveryPreferencePrompt, setPendingDeliveryPreferencePrompt] = useState(false);
  const [deliveryPreferenceError, setDeliveryPreferenceError] = useState("");
  const [savingDeliveryPreference, setSavingDeliveryPreference] = useState<DeliveryMode | null>(null);
  const { startProcessing, stopProcessing } = useProcessing();
  const isAdminRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
  const isAdminSession = user?.role === "ROLE_ADMIN";
  const isCustomerOnboardingBlocked = Boolean(
    (user?.role === "ROLE_CUSTOMER" &&
      (showProfileCompletionModal || showDeliveryPreferenceModal || showPincodeCheckerModal)) ||
      showGuestPincodeCheckerModal ||
      Boolean(guestDeliveryPincodePreview),
  );

  const isPopupOpen =
    showProfileCompletionModal ||
    showDeliveryPreferenceModal ||
    showPincodeCheckerModal ||
    showGuestPincodeCheckerModal ||
    Boolean(deliveryPincodePreview);

  useBodyScrollLock(isPopupOpen);

  useEffect(() => {
    const storedUser = window.localStorage.getItem(AUTH_STORAGE_KEY);
    const storedToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!storedUser || !storedToken) {
      setUser(null);
      setToken(null);
      setLoading(false);
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser) as AuthUser;
      if (!isTokenValidForUser(storedToken, parsedUser)) {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        setApiAuthToken(null);
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      }

      const isAdminArea = window.location.pathname.startsWith("/admin");
      if (parsedUser.role === "ROLE_ADMIN" && !isAdminArea) {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        setApiAuthToken(null);
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      }

      const requireProfile = shouldRequireCustomerName(parsedUser);
      setUser(parsedUser);
      setToken(storedToken);
      setApiAuthToken(storedToken);
      const profileCompletionSkipped = requireProfile && hasSkippedProfileCompletionThisSession(parsedUser);
      const shouldRequireProfileBeforeDelivery = requireProfile && !profileCompletionSkipped;
      setShowProfileCompletionModal(shouldRequireProfileBeforeDelivery);
      setShowPincodeCheckerModal(false);
      setDeliveryPincodePreview(null);
      setShowGuestPincodeCheckerModal(false);
      setGuestDeliveryPincodePreview(null);
      setShowDeliveryPreferenceModal(shouldShowDeliveryPrompt(parsedUser, shouldRequireProfileBeforeDelivery));
      setPendingDeliveryPreferencePrompt(
        Boolean(parsedUser.role === "ROLE_CUSTOMER" && shouldRequireProfileBeforeDelivery && !isAdminArea),
      );
    } catch {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      setApiAuthToken(null);
      setUser(null);
      setToken(null);
    }

    setLoading(false);
  }, []);

  const persistAuth = useCallback((
    nextUser: AuthUser | null,
    nextToken: string | null,
    options?: {
      promptDeliveryPreference?: boolean;
      requireProfileCompletion?: boolean;
    },
  ) => {
    if (nextUser && nextToken && !isTokenValidForUser(nextToken, nextUser)) {
      nextUser = null;
      nextToken = null;
    }

    const shouldRequireProfileCompletion = Boolean(
      nextUser &&
        nextToken &&
        (options?.requireProfileCompletion || shouldRequireCustomerName(nextUser)),
    );
    const profileCompletionSkipped =
      nextUser && hasSkippedProfileCompletionThisSession(nextUser) && shouldRequireProfileCompletion;
    const shouldPromptDeliveryPreference = Boolean(
      options?.promptDeliveryPreference &&
        shouldShowDeliveryPrompt(nextUser, shouldRequireProfileCompletion && !profileCompletionSkipped),
    );
    const shouldDelayDeliveryPreference = Boolean(
      shouldPromptDeliveryPreference && shouldRequireProfileCompletion && !profileCompletionSkipped,
    );

    setShowProfileCompletionModal(shouldRequireProfileCompletion && !profileCompletionSkipped);
    setShowDeliveryPreferenceModal(
      shouldPromptDeliveryPreference && !shouldDelayDeliveryPreference,
    );
    setShowPincodeCheckerModal(false);
    setDeliveryPincodePreview(null);
    setShowGuestPincodeCheckerModal(false);
    setGuestDeliveryPincodePreview(null);
    setPendingDeliveryPreferencePrompt(shouldDelayDeliveryPreference);
    if (nextUser && nextToken) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextUser));
      window.localStorage.setItem(TOKEN_STORAGE_KEY, nextToken);
      setApiAuthToken(nextToken);
      setUser(nextUser);
      setToken(nextToken);
      setDeliveryPreferenceError("");

      return;
    }

    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    setApiAuthToken(null);
    setUser(null);
    setToken(null);
    setDeliveryPreferenceError("");
  }, []);

  useEffect(() => {
    if (!pendingDeliveryPreferencePrompt || isAdminSession || !user) {
      return;
    }

    setShowDeliveryPreferenceModal(true);
    setShowPincodeCheckerModal(false);
    setDeliveryPincodePreview(null);
    setPendingDeliveryPreferencePrompt(false);
    setDeliveryPreferenceError("");
  }, [isAdminSession, pendingDeliveryPreferencePrompt, user]);

  useEffect(() => {
    if (!isAdminSession) {
      return;
    }

    setShowDeliveryPreferenceModal(false);
    setShowPincodeCheckerModal(false);
    setDeliveryPincodePreview(null);
    setPendingDeliveryPreferencePrompt(false);
    setDeliveryPreferenceError("");
  }, [isAdminSession]);

  useEffect(() => {
    if (!deliveryPincodePreview) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDeliveryPincodePreview(null);
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [deliveryPincodePreview]);

  useEffect(() => {
    if (!guestDeliveryPincodePreview) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setGuestDeliveryPincodePreview(null);
      setShowGuestPincodeCheckerModal(false);
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [guestDeliveryPincodePreview]);

  const googleLogin = async (credential: string) => {
    const processingId = startProcessing({
      title: "Signing you in",
      message: "Verifying your Google account and preparing your session...",
    });
    try {
      const response = await googleLoginRequest({ credential });
      persistAuth(response.user, response.token, {
        promptDeliveryPreference: true,
        requireProfileCompletion: response.requiresProfileCompletion,
      });

      return {};
    } catch (error) {
      return {
        error: extractErrorMessage(error, "Unable to log in with Google right now."),
      };
    } finally {
      stopProcessing(processingId);
    }
  };

  const requestPhoneOtp = async (phoneNumber: string) => {
    try {
      const response = await requestPhoneOtpRequest({ phoneNumber });
      return { data: response };
    } catch (error) {
      return {
        error: extractErrorMessage(error, "Unable to send code right now."),
      };
    }
  };

  const verifyPhoneOtp = async (phoneNumber: string, otp: string) => {
    const processingId = startProcessing({
      title: "Verifying code",
      message: "Checking your code and opening your account...",
    });
    try {
      const response = await verifyPhoneOtpRequest({ phoneNumber, otp });
      persistAuth(response.user, response.token, {
        promptDeliveryPreference: true,
        requireProfileCompletion: response.requiresProfileCompletion,
      });
      return {};
    } catch (error) {
      return {
        error: extractErrorMessage(error, "Unable to verify code right now."),
      };
    } finally {
      stopProcessing(processingId);
    }
  };

  const adminLogin = async (email: string, password: string) => {
    try {
      const response = await adminLoginRequest({ email, password });
      persistAuth(response.user, response.token, {
        requireProfileCompletion: response.requiresProfileCompletion,
      });

      return {};
    } catch (error) {
      return {
        error: extractErrorMessage(error, "Unable to log in right now."),
      };
    }
  };

const completeProfile = async (payload: ProfileCompletionPayload) => {
    const processingId = startProcessing({
      title: "Saving profile",
      message: "Updating your account details...",
    });
    try {
      const normalizedPayload: ProfileCompletionPayload = {
        fullName: payload.fullName.trim().replace(/\s+/g, " "),
        phoneNumber: normalizeIndianMobileNumber(payload.phoneNumber),
        email: payload.email?.trim() || undefined,
      };
      const response = await completeProfileRequest(normalizedPayload);
      persistAuth(response.user, response.token, {
        promptDeliveryPreference: true,
        requireProfileCompletion: response.requiresProfileCompletion,
      });
      setShowProfileCompletionModal(false);
      setShowDeliveryPreferenceModal(shouldShowDeliveryPrompt(response.user, response.requiresProfileCompletion));
      setShowPincodeCheckerModal(false);
      setDeliveryPincodePreview(null);
      return {};
    } catch (error) {
      return {
        error: extractErrorMessage(error, "Unable to save your name right now."),
      };
    } finally {
      stopProcessing(processingId);
    }
  };

  const skipProfileCompletion = useCallback(() => {
    if (!user) {
      return;
    }

    markProfileCompletionSkippedThisSession(user);
    setShowProfileCompletionModal(false);
    setPendingDeliveryPreferencePrompt(false);
    setShowDeliveryPreferenceModal(shouldShowDeliveryPrompt(user, false));
  }, [user]);

  const updateDeliveryPreference = async (mode: DeliveryMode) => {
    setDeliveryPreferenceError("");

    if (user?.role !== "ROLE_CUSTOMER") {
      return {
        error: "Delivery preference is only available for customer accounts.",
      };
    }

    setSavingDeliveryPreference(mode);
    const processingId = startProcessing({
      title: "Saving preference",
      message: "Updating your default fulfilment option...",
    });
    try {
      const response = await updateDeliveryPreferenceRequest({
        preferredDeliveryMode: mode,
      });
      markDeliveryPromptCompletedThisSession(user);
      persistAuth(response.user, response.token);
      setShowDeliveryPreferenceModal(false);
      setPendingDeliveryPreferencePrompt(false);
      setShowPincodeCheckerModal(false);
      if (mode === "HOME_DELIVERY") {
        const savedAddresses = await fetchAddresses().catch(() => []);
        const selectedAddress =
          savedAddresses.find((address) => address.defaultAddress) ??
          savedAddresses[0] ??
          readSelectedAddress(user);
        if (selectedAddress) {
          storeSelectedAddress(response.user, selectedAddress);
        }
        const postalCode = selectedAddress?.postalCode?.trim();
        if (/^\d{6}$/.test(postalCode || "")) {
          window.sessionStorage.removeItem(PINCODE_CHECKER_LOGIN_KEY);
          setDeliveryPincodePreview(postalCode || null);
          setShowPincodeCheckerModal(false);
        } else {
          setDeliveryPincodePreview(null);
          setShowPincodeCheckerModal(true);
        }
      } else {
        setShowPincodeCheckerModal(false);
        setDeliveryPincodePreview(null);
      }
      return {};
    } catch (error) {
      const message = extractErrorMessage(error, "Unable to save delivery preference right now.");
      setDeliveryPreferenceError(message);
      return {
        error: message,
      };
    } finally {
      setSavingDeliveryPreference(null);
      stopProcessing(processingId);
    }
  };

  const logout = useCallback(() => {
    if (user?.role === "ROLE_CUSTOMER") {
      window.sessionStorage.removeItem(getDeliveryPromptCompletedSessionKey(user));
    }
    window.sessionStorage.removeItem(PINCODE_CHECKER_LOGIN_KEY);
    persistAuth(null, null);
    setShowDeliveryPreferenceModal(false);
    setShowPincodeCheckerModal(false);
    setDeliveryPincodePreview(null);
    setShowProfileCompletionModal(false);
    setPendingDeliveryPreferencePrompt(false);
    setDeliveryPreferenceError("");
    setShowGuestPincodeCheckerModal(false);
    setGuestDeliveryPincodePreview(null);
  }, [persistAuth, user]);

  const completeGuestDeliveryPrompt = useCallback(() => {
    setShowGuestPincodeCheckerModal(false);
    setGuestDeliveryPincodePreview(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: Boolean(user && token),
        isAdmin: user?.role === "ROLE_ADMIN",
        loading,
        googleLogin,
        requestPhoneOtp,
        verifyPhoneOtp,
        adminLogin,
        completeProfile,
        skipProfileCompletion,
        updateDeliveryPreference,
        logout,
      }}
    >
      <div
        className={isCustomerOnboardingBlocked ? "auth-onboarding-page-blocked" : undefined}
        aria-hidden={isCustomerOnboardingBlocked}
      >
        {children}
      </div>
      {showProfileCompletionModal && user?.role === "ROLE_CUSTOMER" && !isAdminRoute ? (
        <ProfileCompletionModal onSubmit={completeProfile} onSkip={skipProfileCompletion} />
      ) : null}
      {showDeliveryPreferenceModal && !showProfileCompletionModal && user?.role === "ROLE_CUSTOMER" && !isAdminRoute ? (
        <div className="delivery-preference-modal" role="dialog" aria-modal="true">
          <div className="delivery-preference-modal__card">
            <span className="eyebrow">Delivery preference</span>
            <h2>How should we handle your orders?</h2>
            <p>
              Choose your default fulfilment option so shipping charges and order
              management stay accurate.
            </p>
            <div className="delivery-preference-modal__actions">
              <button
                type="button"
                className="delivery-preference-modal__button"
                disabled={Boolean(savingDeliveryPreference)}
                aria-busy={savingDeliveryPreference === "STORE_PICKUP"}
                onClick={() => void updateDeliveryPreference("STORE_PICKUP")}
              >
                {savingDeliveryPreference === "STORE_PICKUP" ? (
                  <span className="button-loading">
                    <span className="button-loading__spinner" aria-hidden="true" />
                    Applying...
                  </span>
                ) : (
                  "Store pickup"
                )}
              </button>
              <button
                type="button"
                className="delivery-preference-modal__button delivery-preference-modal__button--primary"
                disabled={Boolean(savingDeliveryPreference)}
                aria-busy={savingDeliveryPreference === "HOME_DELIVERY"}
                onClick={() => void updateDeliveryPreference("HOME_DELIVERY")}
              >
                {savingDeliveryPreference === "HOME_DELIVERY" ? (
                  <span className="button-loading">
                    <span className="button-loading__spinner" aria-hidden="true" />
                    Applying...
                  </span>
                ) : (
                  "Home delivery"
                )}
              </button>
            </div>
            {deliveryPreferenceError ? (
              <p className="form-error">{deliveryPreferenceError}</p>
            ) : null}
            <button
              type="button"
              className="delivery-preference-modal__skip"
              disabled={Boolean(savingDeliveryPreference)}
              onClick={() => {
                markDeliveryPromptCompletedThisSession(user);
                setShowDeliveryPreferenceModal(false);
                setPendingDeliveryPreferencePrompt(false);
                setDeliveryPreferenceError("");
              }}
            >
              Skip
            </button>
          </div>
        </div>
      ) : null}
      {showPincodeCheckerModal &&
      !showProfileCompletionModal &&
      !showDeliveryPreferenceModal &&
      user?.role === "ROLE_CUSTOMER" &&
      !isAdminRoute ? (
        <PincodeServiceChecker
          open
          storageKey={PINCODE_CHECKER_LOGIN_KEY}
          onClose={() => setShowPincodeCheckerModal(false)}
        />
      ) : null}
      {showGuestPincodeCheckerModal && !showProfileCompletionModal && !user && !isAdminRoute ? (
        <PincodeServiceChecker
          open
          onClose={completeGuestDeliveryPrompt}
        />
      ) : null}
      {deliveryPincodePreview ? (
        <div className="delivery-preference-modal" role="dialog" aria-modal="true" aria-live="polite">
          <div className="delivery-preference-modal__card delivery-preference-modal__delivery-preview">
            <h2>Using your default pincode from saved address</h2>
            <strong>{deliveryPincodePreview}</strong>
          </div>
        </div>
      ) : null}
    </AuthContext.Provider>
  );
};

const ProfileCompletionModal: React.FC<{
  onSubmit: (payload: ProfileCompletionPayload) => Promise<AuthActionResult>;
  onSkip: () => void;
}> = ({ onSubmit, onSkip }) => {
  const { user } = useAuth();
  const [fullName, setFullName] = useState(user?.fullName || "");
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || "");
  const [email, setEmail] = useState(user?.email || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submitProfile = async (skipOptional = false) => {
    setError("");
    if (!fullName.trim() || !phoneNumber.trim()) {
      setError("Please enter your full name and mobile number to continue.");
      return;
    }

    setSaving(true);
    const result = await onSubmit({
      fullName: fullName.trim(),
      phoneNumber: normalizeIndianMobileNumber(phoneNumber),
      email: skipOptional ? undefined : email.trim() || undefined,
    });
    setSaving(false);
    if (result.error) {
      setError(result.error);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await submitProfile(false);
  };

  return (
    <div className="delivery-preference-modal" role="dialog" aria-modal="true">
      <div className="delivery-preference-modal__card">
        <h2>Complete your profile</h2>
        <form className="form-grid delivery-preference-modal__profile-form" onSubmit={handleSubmit}>
          <label>
            Full name
            <input
              value={fullName}
              onChange={(event) => setFullName(restrictLettersOnly(event.target.value))}
              onPaste={(event) => {
                event.preventDefault();
                setFullName(restrictLettersOnly(event.clipboardData.getData("text")));
              }}
              placeholder="Enter your full name"
              autoFocus
              inputMode="text"
              pattern={"[A-Za-z\\s.'\\-]+"}
              required
            />
          </label>
          <label>
            Mobile number
            <input
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(normalizeIndianMobileNumber(event.target.value))}
              onPaste={(event) => {
                event.preventDefault();
                setPhoneNumber(normalizeIndianMobileNumber(event.clipboardData.getData("text")));
              }}
              placeholder="Enter mobile number"
              inputMode="numeric"
              pattern={"\\d{10}"}
              maxLength={10}
              required
            />
          </label>
          <label>
            Email optional
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
            />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="delivery-preference-modal__actions delivery-preference-modal__actions--profile">
            <button
              type="button"
              className="delivery-preference-modal__skip"
              disabled={saving}
              onClick={onSkip}
            >
              Skip
            </button>
            <button
              type="submit"
              className="delivery-preference-modal__button delivery-preference-modal__button--primary"
              disabled={saving}
              aria-busy={saving}
            >
              {saving ? "Saving..." : "Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
